import type { ReactNode } from 'react'
import { Loader } from '@systemeio/components'
import { Maximize02Icon } from '@systemeio/icons'
import { cn, twJoin } from 'cn'
import { useTranslation } from 'next-i18next/pages'
import { AudioLoaderFile } from 'shared/components/audio-loader-file'
import PlayIcon from 'shared/icons/play-icon'
import { usePostAttachmentsContext } from 'modules/attachments/components/context'
import { filterNotGalleryAttachments } from 'modules/attachments/utils/attachment-utils'
import { playableAttachmentTypes } from 'modules/community/const/attachment-consts'
import type { PostAttachmentType } from 'modules/community/types/post-attachment'
import {
  AttachmentTypeEnum,
  AttachmentUploadStatusEnum,
} from 'modules/community/types/post-attachment'
import { previewFactory } from './preview-factory'
import RemoveAttachmentButton from './remove-button'

interface AttachmentPreviewItemProps {
  className?: string
  previewClassName?: string
  size?: 'original' | 'small'
  attachment: PostAttachmentType
}

export default function AttachmentPreviewItem({
  className,
  previewClassName,
  size = 'small',
  attachment,
}: AttachmentPreviewItemProps): ReactNode {
  const { t } = useTranslation()
  const { isEditable, attachments, setInitialGallerySlide, setIsGalleryVisible } =
    usePostAttachmentsContext()
  const isLoading = attachment.status === AttachmentUploadStatusEnum.Uploading
  const isFailed = attachment.status === AttachmentUploadStatusEnum.Failed
  const isPlayable = playableAttachmentTypes.includes(attachment.type) && !isLoading && !isFailed

  return (
    <div
      data-testid="attachment-preview-item"
      className={cn(
        `group relative aspect-square size-24 overflow-hidden md:size-32`,
        isLoading && 'cursor-wait',
        className,
      )}
    >
      {previewFactory(attachment, previewClassName, size)}
      <div
        aria-hidden
        className={twJoin(
          `absolute top-0 left-0 z-10 size-full rounded-lg bg-black opacity-0 transition-all`,
          !isLoading && 'md:group-hover:opacity-75',
        )}
      />
      {isLoading && attachment.type !== AttachmentTypeEnum.Audio && (
        <Loader rootProps={{ className: 'absolute top-1/2 left-1/2 size-16 -translate-1/2' }} />
      )}
      {isLoading && attachment.type === AttachmentTypeEnum.Audio && (
        <AudioLoaderFile
          iconClassName="size-16"
          className="absolute top-1/2 left-1/2 -translate-1/2 md:size-8 lg:size-16"
        />
      )}
      {isPlayable && <PlayIcon className="absolute top-1/2 left-1/2 z-10 w-10 -translate-1/2" />}
      {!isLoading && (
        <>
          {!isFailed && attachment.type !== AttachmentTypeEnum.Audio && (
            <button
              type="button"
              data-testid="attachment-open-gallery"
              aria-label={t('post.attachments.open_gallery')}
              onClick={event => {
                event.stopPropagation()
                setInitialGallerySlide(
                  filterNotGalleryAttachments(attachments).findIndex(
                    ({ id }) => id === attachment.id,
                  ),
                )
                setIsGalleryVisible(true)
              }}
              className={twJoin(
                `absolute top-1/2 left-1/2 z-20 -translate-1/2 opacity-0 group-hover:opacity-100`,
                isEditable && `hidden md:block`,
              )}
            >
              <Maximize02Icon className="size-8 text-text-on" />
            </button>
          )}
          {isEditable && (
            <RemoveAttachmentButton
              id={attachment.id}
              className="absolute top-2 right-2 z-20 opacity-100 transition-all group-hover:top-2 group-hover:opacity-100 md:-top-2 md:opacity-0"
            />
          )}
        </>
      )}
    </div>
  )
}
